import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';

import BottomNav from './BottomNav';
import Sidebar from './Sidebar';
import LandingPage from './LandingPage';
import Login from './Login';
import SignUp from './SignUp';
import ConfirmSignUp from './ConfirmSignUp';
import ForgotPassword from './ForgotPassword';
import InspectionsLayout from './InspectionsLayout';
import RecordingWizard from './RecordingWizard';
import EnrichmentSettings from './EnrichmentSettings';
import TestEnrichment from './TestEnrichment';
import ConsentModal from './ConsentModal';
import AdminDashboard from './AdminDashboard';
import DebugConsole from './DebugConsole';
import { releaseStream } from '../services/microphoneService';
import { fetchUserSettings, updateUserConsent } from '../services/inspectionService';
import { voiceTriggerService } from '../services/voiceTriggerService';
import apiClient from '../services/apiClient';

// Toast/Snackbar component
const Toast = ({ message, severity, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = {
    success: 'bg-success',
    error: 'bg-error',
    warning: 'bg-warning',
    info: 'bg-secondary',
  }[severity] || 'bg-secondary';

  return (
    <div className={`fixed bottom-20 left-4 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg z-[1200] flex items-center justify-between animate-slide-up`}>
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80 cursor-pointer">
        <X size={18} />
      </button>
    </div>
  );
};

const AppShell = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authScreen, setAuthScreen] = useState('landing'); // 'landing', 'login', 'signup', 'confirm', 'forgot'
  const [signUpEmail, setSignUpEmail] = useState('');
  const [snackbar, setSnackbar] = useState({ show: false, message: '', severity: 'success' });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Proactive token refresh when tab becomes visible
  // Browsers throttle setInterval in inactive tabs, so we use visibilitychange
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const session = await fetchAuthSession();
          const exp = session.tokens?.idToken?.payload?.exp;
          const timeUntilExpiry = exp ? (exp * 1000 - Date.now()) : 0;

          // Refresh if less than 5 minutes remaining
          if (!exp || timeUntilExpiry < 5 * 60 * 1000) {
            console.log('Token expiring soon, refreshing...');
            await fetchAuthSession({ forceRefresh: true });
          }
        } catch (error) {
          console.error('Session refresh failed, signing out:', error);
          await signOut();
          setUser(null);
          setAuthScreen('landing');
          navigate('/');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, navigate]);

  const checkAuthStatus = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      // Check if user needs to set consent and sync voice trigger settings
      try {
        const settings = await fetchUserSettings();
        // Sync voice trigger phrases from backend
        voiceTriggerService.syncFromBackend(settings);
        if (settings.dataShareConsent === null || settings.dataShareConsent === undefined) {
          setShowConsentModal(true);
        }
      } catch (settingsError) {
        // If settings fetch fails, show consent modal to be safe
        console.error('Error fetching settings for consent check:', settingsError);
      }

      // Check if user is admin by trying to access admin endpoint
      try {
        await apiClient.get('/admin/inspections?pageSize=1');
        setIsAdmin(true);
      } catch (adminError) {
        // 403 means not admin, which is expected for most users
        setIsAdmin(false);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    checkAuthStatus();
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      releaseStream(); // Release microphone before logout
      await signOut();
      setUser(null);
      setAuthScreen('landing');
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const showMessage = (message, severity = 'success') => {
    // Disabled - no toast notifications
    // setSnackbar({ show: true, message, severity });
  };

  const handleConsentAccept = async () => {
    try {
      await updateUserConsent(true);
      setShowConsentModal(false);
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  };

  const handleConsentDecline = async () => {
    try {
      await updateUserConsent(false);
      setShowConsentModal(false);
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, show: false });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Not authenticated - show auth screens
  if (!user) {
    switch (authScreen) {
      case 'landing':
        return (
          <LandingPage
            onGetStarted={() => setAuthScreen('login')}
          />
        );
      case 'signup':
        return (
          <SignUp
            onSignUpSuccess={(email) => {
              setSignUpEmail(email);
              setAuthScreen('confirm');
            }}
            onBackToLogin={() => setAuthScreen('login')}
          />
        );
      case 'confirm':
        return (
          <ConfirmSignUp
            email={signUpEmail}
            onConfirmSuccess={() => {
              setAuthScreen('login');
            }}
            onBackToLogin={() => setAuthScreen('login')}
          />
        );
      case 'forgot':
        return (
          <ForgotPassword
            onBack={() => setAuthScreen('login')}
            onResetSuccess={() => {
              setAuthScreen('login');
              showMessage(t('auth.reset_password'), 'success');
            }}
          />
        );
      default:
        return (
          <Login
            onLoginSuccess={handleLoginSuccess}
            onForgotPassword={() => setAuthScreen('forgot')}
            onSignUp={() => setAuthScreen('signup')}
          />
        );
    }
  }

  // Authenticated - show app with router
  return (
    <div className="app-layout">
      {/* Desktop sidebar - hidden on mobile */}
      <Sidebar showAdmin={isAdmin} />

      {/* Main content area */}
      <main className="main-content">
        <Routes>
          <Route
            path="/"
            element={
              <InspectionsLayout
                onLogout={handleLogout}
                showMessage={showMessage}
                isAdmin={isAdmin}
              />
            }
          />
          <Route
            path="/record"
            element={
              <RecordingWizard
                onLogout={handleLogout}
                showMessage={showMessage}
                isAdmin={isAdmin}
              />
            }
          />
          <Route
            path="/record/:inspectionId"
            element={
              <RecordingWizard
                onLogout={handleLogout}
                showMessage={showMessage}
                isAdmin={isAdmin}
              />
            }
          />
          <Route
            path="/inspections/:id"
            element={
              <InspectionsLayout
                onLogout={handleLogout}
                showMessage={showMessage}
                isAdmin={isAdmin}
              />
            }
          />
          <Route
            path="/settings"
            element={
              <EnrichmentSettings
                onLogout={handleLogout}
                showMessage={showMessage}
                isAdmin={isAdmin}
              />
            }
          />
          <Route
            path="/settings/test"
            element={
              <TestEnrichment
                onLogout={handleLogout}
                isAdmin={isAdmin}
              />
            }
          />
          <Route
            path="/admin/*"
            element={
              <AdminDashboard
                onLogout={handleLogout}
                isAdmin={isAdmin}
              />
            }
          />
          <Route
            path="/debug"
            element={
              <DebugConsole
                onLogout={handleLogout}
                isAdmin={isAdmin}
              />
            }
          />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Mobile bottom nav - hidden on desktop */}
      <BottomNav showAdmin={isAdmin} />

      {snackbar.show && (
        <Toast
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
        />
      )}

      {showConsentModal && (
        <ConsentModal
          onConsent={handleConsentAccept}
          onDecline={handleConsentDecline}
        />
      )}
    </div>
  );
};

export default AppShell;
